import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MDButton from '@components/MDButton';
import MultiSelectStandalone from '@components/MultiSelectStandalone';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { useTags } from '@layouts/pages/Settings/general/tags/hooks/use-tags.hook';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmLeadService } from '../../kanban-board/services/crm-lead.service';
import { LeadResponse } from '../../kanban-board/services/responses/lead.response';
import { CrmTagChip } from '../../kanban-board/components/shared/crm-tag-chip';

interface Props {
    lead: LeadResponse;
    onChanged: () => void;
}

/// As tags do lead e o botão de escolha; salvar manda o conjunto inteiro, que o servidor substitui.
export const LeadTags = ({ lead, onChanged }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const { tags } = useTags();

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const current = lead.tags ?? [];

    // Só ao abrir: se o lead recarregasse com a janela aberta, a escolha em andamento sumiria.
    useEffect(() => {
        if (open) setSelected(current.map((tag) => tag.id));
    }, [open]);

    const save = async () => {
        setSaving(true);

        try {
            await new CrmLeadService().setTags(lead.id, selected);
            showAlert(t('general.atualizacao'), 'success', 2000);
            setOpen(false);
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {current.map((tag) => (
                <CrmTagChip key={tag.id} name={tag.name} color={tag.color} />
            ))}

            <Tooltip title={t('telaLead.editarTags')}>
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpen(true)}
                    onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        height: 22,
                        px: 1,
                        borderRadius: '999px',
                        border: `1px dashed ${cc.borderStrong}`,
                        color: cc.textSecondary,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { borderColor: cc.borderHover, color: cc.textPrimary },
                    }}
                >
                    <LocalOfferOutlinedIcon sx={{ fontSize: 13 }} />
                    {current.length === 0 && t('telaLead.tags')}
                </Box>
            </Tooltip>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>
                    {t('telaLead.editarTags')}
                    <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                        {lead.contactName}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers sx={{ pt: 2 }}>
                    {tags.length === 0 ? (
                        <Typography variant="body2" sx={{ color: cc.textSecondary }}>
                            {t('telaLead.semTagsCadastradas')}
                        </Typography>
                    ) : (
                        <MultiSelectStandalone
                            autoFocus
                            options={tags.map((tag) => ({ value: tag.id, label: tag.name }))}
                            value={selected}
                            onChange={(values) => setSelected(values.map(String))}
                            placeholder={t('telaLead.escolherTags')}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <MDButton variant="gradient" color="dark" onClick={() => setOpen(false)}>
                        {t('general.cancelar')}
                    </MDButton>
                    <MDButton variant="gradient" color="success" onClick={save} disabled={saving}>
                        {t('general.salvar')}
                    </MDButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
